/**
 * Pré-análise da modalidade assinatura eletrônica (E74.F1, ADR-0075; E78.F3).
 *
 * Mesma etapa da esteira que a triagem visual — "Análise preliminar" —, mas o
 * trabalho é outro: o escritório transcreve o protocolo da plataforma e manda
 * verificar. O servidor recomputa o código de verificação (SHA-256) de cada
 * peça, confronta com o declarado, confere identidade e IDs, monta a linha do
 * tempo e devolve o quadro. Não há triagem visual nem LR aqui.
 *
 * A tela deixou de ser N cards repetidos na E78.F3, e desde a E80.F1 tem quatro
 * partes:
 *   1. o protocolo do caso colapsado (ADR-0077: ele é do processo, não da peça);
 *   2. as fotos do signatário (E80.F1, ADR-0079) — logo abaixo do protocolo,
 *      porque é depois de importá-lo que o perito tem o dossiê em mãos —, e a
 *      conferência facial (E80.F2) na sequência: primeiro se escolhe o par,
 *      depois se lê o que a comparação observou;
 *   3. a tabela das peças, onde se edita o único dado que é da peça — o código
 *      declarado — e se escolhe qual quadro ler;
 *   4. o quadro por eixos da peça aberta.
 *
 * O rascunho de cada peça guarda só o que o usuário editou; o valor gravado
 * continua sendo a fonte enquanto ninguém digita, e a gravação bem-sucedida
 * descarta o rascunho. Sem isso, recarregar a lista depois de salvar deixaria
 * na tela o texto antigo por cima do que o servidor acabou de confirmar.
 *
 * Montada pelo `PreAnalisePage` quando `case.modalidade === 'eletronica'`; a
 * página recebe o caso já carregado e não o busca de novo.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CaseStageStepper } from '@/components/Casos/CaseStageStepper';
import { ConferenciaFacial } from '@/components/ExameEletronico/ConferenciaFacial';
import { FotosDoSignatario } from '@/components/ExameEletronico/FotosDoSignatario';
import { PecaSelecionada } from '@/components/ExameEletronico/PecaSelecionada';
import { ProtocoloDoCasoCard } from '@/components/ExameEletronico/ProtocoloDoCasoCard';
import type { ProtocoloFormAcao } from '@/components/ExameEletronico/ProtocoloForm';
import { ptBR } from '@/i18n/pt-BR';
import { PreAnaliseAvisos, SecaoDePecas } from '@/pages/PreAnaliseEletronicaStates';
import { useApiClient } from '@/lib/api';
import type { WorkflowStatus } from '@/lib/cases';
import {
  ExameEletronicoError,
  listExamesEletronicos,
  protocoloDoCaso,
  salvarCodigoDaPeca,
  salvarProtocoloDoCaso,
  verificarCaso,
  type ExameEletronicoCasoData,
  type ExameEletronicoCcbData,
  type ExameEletronicoErrorReason,
  type ProtocoloAssinaturaData,
} from '@/lib/exame_eletronico';
import { codigoValido, normalizarCodigo } from '@/lib/exame_eletronico_peca';

const t = ptBR.exameEletronico;
const ti = ptBR.exameEletronico.importarProtocolo;

const tc = ptBR.exameEletronico.conferenciaFacial;

const _ERROR_MESSAGE: Record<ExameEletronicoErrorReason, string> = {
  case_not_found: t.errorCaseNotFound,
  document_not_found: t.errorDocumentNotFound,
  not_found: t.errorNotFound,
  modalidade_invalida: t.errorModalidadeInvalida,
  sem_protocolo: t.errorSemProtocolo,
  nao_verificado: t.errorNaoVerificado,
  justificativa_requerida: t.errorJustificativaRequerida,
  concluido: t.errorConcluido,
  nao_concluido: t.errorNaoConcluido,
  certificacao_requerida: t.errorCertificacaoRequerida,
  protocolo_peca_concluida: t.errorProtocoloPecaConcluida,
  protocolo_nao_importado: ti.erros.protocolo_nao_importado,
  documento_ilegivel: ti.erros.documento_ilegivel,
  documento_sem_texto: ti.erros.documento_sem_texto,
  arquivo_nao_suportado: ti.erros.arquivo_nao_suportado,
  arquivo_vazio: ti.erros.arquivo_vazio,
  arquivo_muito_grande: ti.erros.arquivo_muito_grande,
  arquivo_nao_e_imagem: ptBR.exameEletronico.fotosDoSignatario.erros.arquivo_nao_e_imagem,
  // E80.F2 — as recusas da conferência facial. Vivem no mesmo vocabulário de
  // erro da raia eletrônica: um segundo classificador só para elas duplicaria a
  // leitura da resposta do servidor para ganhar cinco chaves.
  foto_integridade: tc.erros.foto_integridade,
  foto_indisponivel: tc.erros.foto_indisponivel,
  conferencia_sem_par: tc.erros.conferencia_sem_par,
  conferencia_indisponivel: tc.erros.conferencia_indisponivel,
  provider_auth: tc.erros.provider_auth,
  provider_credit: tc.erros.provider_credit,
  sem_chave: tc.erros.sem_chave,
  validation: t.errorValidation,
  forbidden: t.errorForbidden,
  network: t.errorNetwork,
  unknown: t.errorGeneric,
};

function messageFor(err: unknown): string {
  if (err instanceof ExameEletronicoError) return _ERROR_MESSAGE[err.reason];
  return t.errorGeneric;
}

type PorPeca<T> = Readonly<Record<string, T>>;

function semChave<T>(mapa: PorPeca<T>, chave: string): PorPeca<T> {
  const { [chave]: descartado, ...resto } = mapa;
  void descartado;
  return resto;
}

/** A peça que abre por padrão: a primeira já verificada; senão, a primeira. */
function pecaDeAbertura(pecas: readonly ExameEletronicoCcbData[]): string | null {
  const comQuadro = pecas.find((peca) => peca.exame?.resultado != null);
  return (comQuadro ?? pecas[0])?.source_document_id ?? null;
}

export interface PreAnaliseEletronicaPageProps {
  caseId: string;
  workflowStatus: WorkflowStatus | null;
}

export function PreAnaliseEletronicaPage({
  caseId,
  workflowStatus,
}: PreAnaliseEletronicaPageProps): JSX.Element {
  const client = useApiClient();
  const [caso, setCaso] = useState<ExameEletronicoCasoData | null>(null);
  const [erroGlobal, setErroGlobal] = useState<string | null>(null);
  const [sucessoGlobal, setSucessoGlobal] = useState<string | null>(null);
  const [acaoDoCaso, setAcaoDoCaso] = useState<ProtocoloFormAcao | null>(null);
  const [salvandoPeca, setSalvandoPeca] = useState<string | null>(null);
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [codigosEditados, setCodigosEditados] = useState<PorPeca<string>>({});
  const [observacoesEditadas, setObservacoesEditadas] = useState<PorPeca<string>>({});
  const [erroPorPeca, setErroPorPeca] = useState<PorPeca<string>>({});
  // Contador, e não o conteúdo das fotos: a conferência só precisa saber QUE
  // mudou, e passar a lista faria a seção de baixo reler a cada render.
  const [versaoDasFotos, setVersaoDasFotos] = useState(0);
  const [sucessoPorPeca, setSucessoPorPeca] = useState<PorPeca<string>>({});

  const recarregar = useCallback(async (): Promise<void> => {
    setCaso(await listExamesEletronicos(client, caseId));
  }, [client, caseId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const lista = await listExamesEletronicos(client, caseId);
        if (cancelled) return;
        setCaso(lista);
        setSelecionada((atual) => atual ?? pecaDeAbertura(lista.pecas));
      } catch (err) {
        if (!cancelled) setErroGlobal(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, caseId]);

  const pecas = useMemo(() => caso?.pecas ?? [], [caso]);
  const algumaConcluida = pecas.some((p) => p.exame?.status === 'concluido');

  /** O valor em tela: o rascunho quando houver, o gravado enquanto não houver. */
  const codigos = useMemo(
    () =>
      Object.fromEntries(
        pecas.map((peca) => [
          peca.source_document_id,
          codigosEditados[peca.source_document_id] ?? peca.codigo_declarado ?? '',
        ]),
      ),
    [pecas, codigosEditados],
  );

  /**
   * Uma ação de caso por vez: o card trava enquanto está em voo, mas o guard
   * fica aqui também — é a página que conhece o estado, não o botão.
   */
  const executarNoCaso = useCallback(
    async (acao: ProtocoloFormAcao, chamada: () => Promise<unknown>, sucesso: string | null) => {
      if (acaoDoCaso !== null) return;
      setAcaoDoCaso(acao);
      setErroGlobal(null);
      setSucessoGlobal(null);
      try {
        await chamada();
        await recarregar();
        if (sucesso !== null) setSucessoGlobal(sucesso);
      } catch (err) {
        setErroGlobal(messageFor(err));
      } finally {
        setAcaoDoCaso(null);
      }
    },
    [acaoDoCaso, recarregar],
  );

  const handleSalvarProtocolo = (protocolo: ProtocoloAssinaturaData): void => {
    void executarNoCaso(
      'salvando',
      () => salvarProtocoloDoCaso(client, caseId, protocoloDoCaso(protocolo)),
      t.protocoloSalvo,
    );
  };

  const handleVerificar = (): void => {
    void executarNoCaso('verificando', () => verificarCaso(client, caseId), null);
  };

  /** Mesma verificação, refazendo as consultas às fontes externas (gate de custo). */
  const handleAtualizarConsultas = (): void => {
    void executarNoCaso(
      'atualizando',
      () => verificarCaso(client, caseId, { atualizarConsultas: true }),
      null,
    );
  };

  const handleSalvarPeca = (docId: string): void => {
    if (salvandoPeca !== null) return;
    const peca = pecas.find((p) => p.source_document_id === docId);
    if (peca === undefined) return;
    const codigo = codigos[docId] ?? '';
    if (!codigoValido(codigo)) {
      setErroPorPeca((atuais) => ({ ...atuais, [docId]: t.form.hashDeclaradoErro }));
      return;
    }
    const normalizado = normalizarCodigo(codigo);
    const observacoes = (observacoesEditadas[docId] ?? peca.observacoes_peca ?? '').trim();
    void (async () => {
      setSalvandoPeca(docId);
      setErroPorPeca((atuais) => semChave(atuais, docId));
      setSucessoPorPeca((atuais) => semChave(atuais, docId));
      try {
        await salvarCodigoDaPeca(client, caseId, docId, {
          codigo_declarado: normalizado.length > 0 ? normalizado : null,
          observacoes: observacoes.length > 0 ? observacoes : null,
        });
        await recarregar();
        // O gravado volta a ser a fonte: o rascunho desta peça deixa de existir.
        setCodigosEditados((atuais) => semChave(atuais, docId));
        setObservacoesEditadas((atuais) => semChave(atuais, docId));
        setSucessoPorPeca((atuais) => ({ ...atuais, [docId]: t.codigoDaPecaSalvo }));
      } catch (err) {
        setErroPorPeca((atuais) => ({ ...atuais, [docId]: messageFor(err) }));
      } finally {
        setSalvandoPeca(null);
      }
    })();
  };

  // Identidade estável: um callback novo a cada render reagendaria o efeito da
  // conferência sem parar (lição do mock instável que virava laço).
  const marcarFotosMudadas = useCallback((): void => {
    setVersaoDasFotos((atual) => atual + 1);
  }, []);

  const editarCodigo = useCallback((docId: string, codigo: string): void => {
    setCodigosEditados((atuais) => ({ ...atuais, [docId]: codigo }));
  }, []);

  const indiceSelecionado = pecas.findIndex((p) => p.source_document_id === selecionada);
  const pecaAberta = indiceSelecionado >= 0 ? pecas[indiceSelecionado] : undefined;

  return (
    <section
      className="mx-auto max-w-6xl"
      data-testid="pre-analise-page"
      data-modalidade="eletronica"
      aria-labelledby="pre-analise-title"
    >
      <h1 id="pre-analise-title" className="text-display-md text-fg">
        {t.pageTitle}
      </h1>
      <p className="mt-1 max-w-prose text-body-md text-fg-muted">{t.pageSubtitle}</p>

      <div className="mt-sc-4">
        <CaseStageStepper
          caseId={caseId}
          workflowStatus={workflowStatus}
          activeStage="preliminar"
          modalidade="eletronica"
        />
      </div>

      <PreAnaliseAvisos erroGlobal={erroGlobal} sucessoGlobal={sucessoGlobal} caso={caso} />

      {caso !== null ? (
        <div className="mt-sc-6">
          <ProtocoloDoCasoCard
            caseId={caseId}
            protocolo={caso.protocolo}
            totalDePecas={pecas.length}
            acaoEmCurso={acaoDoCaso}
            concluido={algumaConcluida}
            onSalvar={handleSalvarProtocolo}
            onVerificar={handleVerificar}
            onAtualizarConsultas={handleAtualizarConsultas}
          />
        </div>
      ) : null}

      {/* As duas seções do signatário andam juntas: escolher o par (E80.F1) e
          ler o que a comparação observou (E80.F2) são o mesmo passo do
          trabalho, e separá-las em dois testes de presença só acrescentaria um
          ramo à página sem mudar o que aparece. */}
      {caso !== null ? (
        <div className="mt-sc-6 space-y-sc-6">
          <FotosDoSignatario
            caseId={caseId}
            concluido={algumaConcluida}
            onMudou={marcarFotosMudadas}
          />
          <ConferenciaFacial
            caseId={caseId}
            concluido={algumaConcluida}
            versaoDasFotos={versaoDasFotos}
          />
        </div>
      ) : null}

      {pecas.length > 0 ? (
        <SecaoDePecas
          pecas={pecas}
          selecionada={selecionada}
          rascunhos={codigos}
          salvando={salvandoPeca}
          erroPorPeca={erroPorPeca}
          onSelecionar={setSelecionada}
          onEditarCodigo={editarCodigo}
          onSalvarPeca={handleSalvarPeca}
        />
      ) : null}

      {pecaAberta !== undefined ? (
        <div className="mt-sc-6">
          <PecaSelecionada
            key={pecaAberta.source_document_id}
            peca={pecaAberta}
            indice={indiceSelecionado}
            total={pecas.length}
            observacoes={
              observacoesEditadas[pecaAberta.source_document_id] ??
              pecaAberta.observacoes_peca ??
              ''
            }
            salvando={salvandoPeca === pecaAberta.source_document_id}
            erro={erroPorPeca[pecaAberta.source_document_id] ?? null}
            sucesso={sucessoPorPeca[pecaAberta.source_document_id] ?? null}
            onEditarObservacoes={(valor) =>
              setObservacoesEditadas((atuais) => ({
                ...atuais,
                [pecaAberta.source_document_id]: valor,
              }))
            }
            onSalvar={() => handleSalvarPeca(pecaAberta.source_document_id)}
          />
        </div>
      ) : null}
    </section>
  );
}
